import { useCallback, useEffect, useRef, useState } from 'react';

export const EQ_BANDS = [
  { id: 'sub', label: 'Sub', frequency: 80, type: 'lowshelf' },
  { id: 'bass', label: 'Bass', frequency: 250, type: 'peaking' },
  { id: 'mid', label: 'Mid', frequency: 1000, type: 'peaking' },
  { id: 'presence', label: 'Presence', frequency: 4000, type: 'peaking' },
  { id: 'treble', label: 'Treble', frequency: 12000, type: 'highshelf' },
];

export const EQ_PRESETS = {
  flat: { sub: 0, bass: 0, mid: 0, presence: 0, treble: 0 },
  bass: { sub: 4, bass: 5, mid: 0, presence: -1, treble: -2 },
  podcast: { sub: -2, bass: 2, mid: 4, presence: 3, treble: -1 },
  bright: { sub: -1, bass: 0, mid: 1, presence: 4, treble: 5 },
};

const DEFAULT_GAINS = { ...EQ_PRESETS.flat };

export function normalizeEqGains(gains) {
  const base = { ...EQ_PRESETS.flat };
  EQ_BANDS.forEach((band) => {
    const v = Number(gains?.[band.id]);
    if (Number.isFinite(v)) {
      base[band.id] = Math.min(12, Math.max(-12, v));
    }
  });
  return base;
}

export function isFlatEqGains(gains) {
  return EQ_BANDS.every((band) => Math.abs(Number(gains?.[band.id]) || 0) < 0.001);
}

function applyGainsToFilters(filters, gains) {
  EQ_BANDS.forEach((band) => {
    const filter = filters[band.id];
    if (filter) filter.gain.value = Number(gains[band.id]) || 0;
  });
}

/**
 * Routes media element audio through Web Audio biquad filters + analyser (no external packages).
 * Graph stays connected once created (do not tear down when UI closes — MediaElementSource is one-shot).
 */
export function useWebAudioEqualizer(audioRef, connect, { controlledGains, onGainsChange } = {}) {
  const controlled = controlledGains != null && typeof onGainsChange === 'function';
  const [internalGains, setInternalGains] = useState(DEFAULT_GAINS);
  const gains = controlled ? normalizeEqGains(controlledGains) : internalGains;

  const ctxRef = useRef(null);
  const filtersRef = useRef({});
  const analyserRef = useRef(null);
  const wasPlayingBeforeHideRef = useRef(false);
  const [ready, setReady] = useState(false);

  const commitGains = useCallback(
    (next) => {
      const normalized = normalizeEqGains(typeof next === 'function' ? next(gains) : next);
      if (controlled) onGainsChange(normalized);
      else setInternalGains(normalized);
    },
    [controlled, gains, onGainsChange]
  );

  useEffect(() => {
    if (!connect) return undefined;
    const el = audioRef.current;
    if (!el) return undefined;

    let cancelled = false;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return undefined;

    const ctx = new AudioCtx({ latencyHint: 'playback' });
    ctxRef.current = ctx;

    try {
      const source = ctx.createMediaElementSource(el);
      const filters = {};
      let node = source;
      EQ_BANDS.forEach((band) => {
        const filter = ctx.createBiquadFilter();
        filter.type = band.type;
        filter.frequency.value = band.frequency;
        filter.Q.value = band.type === 'peaking' ? 1.1 : 0.7;
        filter.gain.value = 0;
        node.connect(filter);
        node = filter;
        filters[band.id] = filter;
      });
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 64;
      node.connect(analyser);
      analyser.connect(ctx.destination);
      filtersRef.current = filters;
      analyserRef.current = analyser;
      applyGainsToFilters(filters, gains);
      if (!cancelled) setReady(true);
    } catch {
      if (!cancelled) setReady(false);
    }

    const resumeGraphAndPlayback = async () => {
      if (el.paused && !wasPlayingBeforeHideRef.current) return;
      try {
        if (ctx.state === 'suspended') await ctx.resume();
      } catch {
        /* ignore */
      }
      if (wasPlayingBeforeHideRef.current && el.paused) {
        try {
          await el.play();
        } catch {
          /* gesture / policy */
        }
      }
    };

    const resumeOnPlay = () => {
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    };
    el.addEventListener('play', resumeOnPlay);

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        wasPlayingBeforeHideRef.current = !el.paused;
        return;
      }
      resumeGraphAndPlayback();
    };

    const onPageShow = () => {
      resumeGraphAndPlayback();
    };

    ctx.addEventListener('statechange', () => {
      if (ctx.state === 'suspended' && !el.paused) {
        ctx.resume().catch(() => {});
      }
    });

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', resumeGraphAndPlayback);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', resumeGraphAndPlayback);
      el.removeEventListener('play', resumeOnPlay);
      filtersRef.current = {};
      analyserRef.current = null;
      ctx.close().catch(() => {});
      ctxRef.current = null;
      setReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- wire once when connect latches true
  }, [connect, audioRef]);

  useEffect(() => {
    if (!ready) return;
    applyGainsToFilters(filtersRef.current, gains);
  }, [gains, ready]);

  const setBandGain = useCallback(
    (bandId, value) => {
      commitGains((prev) => ({ ...prev, [bandId]: value }));
    },
    [commitGains]
  );

  const applyPreset = useCallback(
    (presetId) => {
      const preset = EQ_PRESETS[presetId] || EQ_PRESETS.flat;
      commitGains({ ...preset });
    },
    [commitGains]
  );

  const resetEq = useCallback(() => {
    commitGains({ ...EQ_PRESETS.flat });
  }, [commitGains]);

  return {
    ready,
    gains,
    setBandGain,
    applyPreset,
    resetEq,
    analyserRef,
  };
}
